// frontend/src/pages/OpsTenants.jsx
import { useCallback, useEffect, useMemo, useState } from "react";
import ErrorBoundary from "../components/ErrorBoundary.jsx";
import { createClerkOrgForTenant, fetchOperatorTenants, inviteTenantMembers, updateOperatorTenant } from "../api.js";

const STATUS_LABELS = {
  active: "Active",
  suspended: "Suspended",
  trial: "Trial",
  closed: "Closed",
};

function formatDateTime(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
}

function formatStatus(status) {
  if (!status) return "Unknown";
  return STATUS_LABELS[status] || status;
}

function getStatusColor(status) {
  switch (status) {
    case "active":
      return { bg: "var(--success-bg)", color: "var(--success)", border: "var(--success)" };
    case "trial":
      return { bg: "var(--info-bg)", color: "var(--info)", border: "var(--info)" };
    case "suspended":
      return { bg: "var(--warning-bg)", color: "var(--warning)", border: "var(--warning)" };
    case "closed":
      return { bg: "var(--danger-bg)", color: "var(--danger)", border: "var(--danger)" };
    default:
      return { bg: "var(--surface-elevated)", color: "var(--text-muted)", border: "var(--border-primary)" };
  }
}

function getHealthState(tenant) {
  const hasClerkOrg = Boolean(tenant.clerkOrgId || tenant.clerk_org_id);
  const memberCount = tenant.memberCount ?? tenant.member_count ?? 0;
  const seats = tenant.seats != null ? Number(tenant.seats) : null;

  const issues = [];
  if (!hasClerkOrg) issues.push("No Clerk org linked");
  if (!memberCount) issues.push("No active members");
  if (seats !== null && seats > 0 && memberCount > seats) issues.push("More members than seats");

  if (!issues.length) {
    return {
      label: "Portal healthy",
      description: "Clerk org linked and at least one active member.",
      color: "var(--success)",
      bg: "var(--success-bg)",
      border: "var(--success)",
    };
  }

  return {
    label: "Attention needed",
    description: issues.join(" · "),
    color: "var(--warning)",
    bg: "var(--warning-bg)",
    border: "var(--warning)",
  };
}

function TenantCard({ tenant, onRefresh }) {
  const [editingSeats, setEditingSeats] = useState(false);
  const [seatsInput, setSeatsInput] = useState(tenant.seats != null ? String(tenant.seats) : "");
  const [savingSeats, setSavingSeats] = useState(false);
  const [seatsError, setSeatsError] = useState(null);

  const [creatingOrg, setCreatingOrg] = useState(false);
  const [clerkEmail, setClerkEmail] = useState("");
  const [actionMessage, setActionMessage] = useState(null);
  const [actionError, setActionError] = useState(null);

  const [inviteEmails, setInviteEmails] = useState("");
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState(null);
  const [inviteMessage, setInviteMessage] = useState(null);

  const hasClerkOrg = Boolean(tenant.clerkOrgId || tenant.clerk_org_id);
  const clerkOrgId = tenant.clerkOrgId || tenant.clerk_org_id || null;
  const memberCount = tenant.memberCount ?? tenant.member_count ?? null;
  const statusColors = getStatusColor(tenant.status);
  const health = getHealthState(tenant);

  const ownerName = tenant.ownerName || tenant.owner_name || null;
  const ownerEmail = tenant.ownerEmail || tenant.owner_email || null;

  const invitesPending = tenant.invitesPending ?? tenant.invites_pending ?? null;
  const invitesAccepted = tenant.invitesAccepted ?? tenant.invites_accepted ?? null;
  const invitesTotal = tenant.invitesTotal ?? tenant.invites_total ?? null;

  const seatUsageLabel = useMemo(() => {
    const seats = tenant.seats != null ? Number(tenant.seats) : null;
    if (!Number.isFinite(seats) || seats <= 0) {
      if (memberCount != null) {
        return `${memberCount} members`;
      }
      return "Seats not configured";
    }
    if (memberCount == null) {
      return `${seats} seats`;
    }
    return `${memberCount} / ${seats} seats used`;
  }, [tenant.seats, memberCount]);

  const handleSeatsSave = async () => {
    setSeatsError(null);
    const value = seatsInput.trim();
    const seats = value === "" ? null : Number(value);
    if (value !== "" && (!Number.isFinite(seats) || seats <= 0)) {
      setSeatsError("Seats must be a positive number or empty.");
      return;
    }

    setSavingSeats(true);
    try {
      await updateOperatorTenant(tenant.id, { seats });
      setEditingSeats(false);
      if (typeof onRefresh === "function") {
        onRefresh();
      }
    } catch (err) {
      console.error("[OpsTenants] Failed to update seats", err);
      setSeatsError(err?.message || "Failed to update seats");
    } finally {
      setSavingSeats(false);
    }
  };

  const handleCreateClerkOrg = async () => {
    setActionMessage(null);
    setActionError(null);

    const email = clerkEmail.trim();
    if (!email) {
      setActionError("Owner email is required to create a Clerk organization.");
      return;
    }

    setCreatingOrg(true);
    try {
      const result = await createClerkOrgForTenant(tenant.id, { email });
      const orgId = result?.tenant?.clerkOrgId || result?.tenant?.clerk_org_id || result?.clerkOrgId || result?.clerk_org_id || clerkOrgId;
      const invites = result?.invitations || result?.invites || [];

      setActionMessage(orgId ? `Clerk org linked (${orgId}). ${invites.length ? `${invites.length} invitation(s) sent.` : ""}` : "Clerk org created and invitations sent.");
      setClerkEmail("");
      if (typeof onRefresh === "function") {
        onRefresh();
      }
    } catch (err) {
      console.error("[OpsTenants] Failed to create Clerk org", err);
      const data = err?.data || {};
      if (data.error === "user_not_found") {
        setActionError("No matching user was found for that email address.");
      } else if (data.error === "organization_exists") {
        setActionError("This tenant already has a Clerk organization linked.");
      } else {
        setActionError(err?.message || "Failed to create Clerk organization.");
      }
    } finally {
      setCreatingOrg(false);
    }
  };

  const handleInviteMembers = async () => {
    setInviteError(null);
    setInviteMessage(null);

    const raw = inviteEmails.trim();
    if (!raw) {
      setInviteError("Enter at least one email address.");
      return;
    }

    const emails = raw
      .split(/[,\s]+/)
      .map((e) => e.trim())
      .filter(Boolean);

    if (!emails.length) {
      setInviteError("Enter at least one valid email address.");
      return;
    }

    setInviting(true);
    try {
      const result = await inviteTenantMembers(tenant.id, { emails });
      const count = result?.count ?? emails.length;
      setInviteMessage(`Invited ${count} user(s).`);
      setInviteEmails("");
      if (typeof onRefresh === "function") {
        onRefresh();
      }
    } catch (err) {
      console.error("[OpsTenants] Failed to invite members", err);
      setInviteError(err?.message || "Failed to invite members.");
    } finally {
      setInviting(false);
    }
  };

  return (
    <div
      style={{
        borderRadius: "var(--radius-lg)",
        border: "1px solid var(--border-primary)",
        backgroundColor: "var(--surface)",
        padding: "var(--space-lg)",
        display: "flex",
        flexDirection: "column",
        gap: "var(--space-lg)",
      }}>
      {/* Header row */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: "var(--space-lg)",
          flexWrap: "wrap",
        }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "var(--space-sm)", marginBottom: "4px" }}>
            <h3
              style={{
                fontSize: "18px",
                fontWeight: "600",
                margin: 0,
                color: "var(--text-primary)",
              }}>
              {tenant.name || "Unnamed tenant"}
            </h3>
            <span
              style={{
                fontSize: "11px",
                fontWeight: "500",
                padding: "2px 8px",
                borderRadius: "999px",
                border: `1px solid ${statusColors.border}`,
                backgroundColor: statusColors.bg,
                color: statusColors.color,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
              }}>
              {formatStatus(tenant.status)}
            </span>
          </div>
          <p style={{ margin: 0, fontSize: "12px", color: "var(--text-secondary)" }}>
            Slug: <code>{tenant.slug}</code>
          </p>
          <p style={{ margin: 0, fontSize: "12px", color: "var(--text-secondary)" }}>Created: {formatDateTime(tenant.createdAt || tenant.created_at)}</p>
          {ownerEmail && (
            <p style={{ margin: 0, fontSize: "12px", color: "var(--text-secondary)" }}>
              Owner:{" "}
              <span style={{ fontWeight: 500 }}>
                {ownerName ? `${ownerName} · ` : ""}
                {ownerEmail}
              </span>
            </p>
          )}
        </div>

        <div style={{ textAlign: "right", minWidth: "220px" }}>
          {/* Health pill */}
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              padding: "2px 10px",
              borderRadius: "999px",
              border: `1px solid ${health.border}`,
              backgroundColor: health.bg,
              color: health.color,
              fontSize: "11px",
              marginBottom: "4px",
            }}>
            <span
              style={{
                width: "8px",
                height: "8px",
                borderRadius: "999px",
                backgroundColor: health.color,
              }}
            />
            <span>{health.label}</span>
          </div>
          <p style={{ margin: 0, fontSize: "11px", color: "var(--text-muted)" }}>{health.description}</p>
          <p style={{ margin: "6px 0 0 0", fontSize: "13px", color: "var(--text-secondary)" }}>{seatUsageLabel}</p>
          <p style={{ margin: 0, fontSize: "11px", color: "var(--text-muted)" }}>Last updated: {formatDateTime(tenant.updatedAt || tenant.updated_at)}</p>
        </div>
      </div>

      {/* Content grid */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1.1fr) minmax(0, 0.9fr)",
          gap: "var(--space-lg)",
        }}>
        {/* Seats & Members */}
        <div
          style={{
            borderRadius: "var(--radius-md)",
            border: "1px solid var(--border-secondary)",
            backgroundColor: "var(--surface-elevated)",
            padding: "var(--space-md)",
          }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "var(--space-sm)",
            }}>
            <span style={{ fontSize: "13px", fontWeight: "600", color: "var(--text-primary)" }}>Seats & Members</span>
            {!editingSeats && (
              <button
                type="button"
                onClick={() => setEditingSeats(true)}
                style={{
                  padding: "4px 10px",
                  fontSize: "11px",
                  borderRadius: "999px",
                  border: "1px solid var(--border-primary)",
                  backgroundColor: "var(--surface)",
                  color: "var(--text-secondary)",
                  cursor: "pointer",
                }}>
                Edit seats
              </button>
            )}
          </div>

          {!editingSeats ? (
            <div>
              <p style={{ margin: 0, fontSize: "22px", fontWeight: "600", color: "var(--text-primary)" }}>{tenant.seats != null ? tenant.seats : "—"}</p>
              <p style={{ margin: "4px 0 0 0", fontSize: "12px", color: "var(--text-secondary)" }}>{seatUsageLabel}</p>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-sm)" }}>
              <div style={{ display: "flex", gap: "var(--space-sm)" }}>
                <input
                  type="number"
                  min="1"
                  value={seatsInput}
                  onChange={(e) => setSeatsInput(e.target.value)}
                  style={{
                    flex: "1 1 0",
                    padding: "6px var(--space-md)",
                    fontSize: "13px",
                    borderRadius: "var(--radius-sm)",
                    border: "1px solid var(--border-primary)",
                    backgroundColor: "var(--surface)",
                    color: "var(--text-primary)",
                  }}
                />
                <button
                  type="button"
                  onClick={handleSeatsSave}
                  disabled={savingSeats}
                  style={{
                    padding: "6px var(--space-md)",
                    backgroundColor: savingSeats ? "var(--text-disabled)" : "var(--accent-primary)",
                    color: "#fff",
                    border: "none",
                    borderRadius: "var(--radius-sm)",
                    fontSize: "13px",
                    fontWeight: "500",
                    cursor: savingSeats ? "not-allowed" : "pointer",
                  }}>
                  {savingSeats ? "Saving…" : "Save"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setEditingSeats(false);
                    setSeatsInput(tenant.seats != null ? String(tenant.seats) : "");
                    setSeatsError(null);
                  }}
                  style={{
                    padding: "6px var(--space-md)",
                    backgroundColor: "var(--surface-elevated)",
                    color: "var(--text-primary)",
                    border: "1px solid var(--border-primary)",
                    borderRadius: "var(--radius-sm)",
                    fontSize: "13px",
                    fontWeight: "500",
                    cursor: "pointer",
                  }}>
                  Cancel
                </button>
              </div>
              {seatsError && <p style={{ margin: 0, fontSize: "12px", color: "var(--danger)" }}>{seatsError}</p>}
            </div>
          )}
        </div>

        {/* Clerk Org & Invitations */}
        <div
          style={{
            borderRadius: "var(--radius-md)",
            border: "1px solid var(--border-secondary)",
            backgroundColor: "var(--surface-elevated)",
            padding: "var(--space-md)",
            display: "flex",
            flexDirection: "column",
            gap: "var(--space-md)",
          }}>
          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "4px",
              }}>
              <span style={{ fontSize: "13px", fontWeight: "600", color: "var(--text-primary)" }}>Clerk Organization</span>
              <span
                style={{
                  fontSize: "11px",
                  padding: "2px 8px",
                  borderRadius: "999px",
                  border: hasClerkOrg ? "1px solid var(--success)" : "1px solid var(--border-primary)",
                  backgroundColor: hasClerkOrg ? "var(--success-bg)" : "var(--surface)",
                  color: hasClerkOrg ? "var(--success)" : "var(--text-secondary)",
                }}>
                {hasClerkOrg ? "Linked" : "Not linked"}
              </span>
            </div>
            {hasClerkOrg && (
              <p style={{ margin: 0, fontSize: "12px", color: "var(--text-secondary)" }}>
                Org ID: <code>{clerkOrgId}</code>
              </p>
            )}
            {hasClerkOrg && invitesTotal != null && (
              <p style={{ margin: "4px 0 0 0", fontSize: "12px", color: "var(--text-secondary)" }}>
                Invitations:{" "}
                <span style={{ fontWeight: 500 }}>
                  {invitesAccepted ?? 0} accepted · {invitesPending ?? 0} pending · {invitesTotal} total
                </span>
              </p>
            )}
          </div>

          {!hasClerkOrg ? (
            <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-xs)" }}>
              <label style={{ fontSize: "12px", color: "var(--text-secondary)" }}>Owner email (will receive initial portal invitation)</label>
              <div style={{ display: "flex", gap: "var(--space-sm)", alignItems: "center" }}>
                <input
                  type="email"
                  value={clerkEmail}
                  onChange={(e) => setClerkEmail(e.target.value)}
                  placeholder="owner@example.com"
                  style={{
                    flex: "1 1 0",
                    padding: "6px var(--space-md)",
                    borderRadius: "var(--radius-sm)",
                    border: "1px solid var(--border-primary)",
                    fontSize: "13px",
                    backgroundColor: "var(--surface)",
                    color: "var(--text-primary)",
                  }}
                />
                <button
                  type="button"
                  onClick={handleCreateClerkOrg}
                  disabled={creatingOrg}
                  style={{
                    padding: "6px var(--space-md)",
                    backgroundColor: creatingOrg ? "var(--text-disabled)" : "var(--accent-primary)",
                    color: "#fff",
                    border: "none",
                    borderRadius: "var(--radius-sm)",
                    fontSize: "13px",
                    fontWeight: "500",
                    cursor: creatingOrg ? "not-allowed" : "pointer",
                    whiteSpace: "nowrap",
                  }}>
                  {creatingOrg ? "Linking…" : "Create & Invite"}
                </button>
              </div>
              {actionError && <p style={{ margin: 0, fontSize: "12px", color: "var(--danger)" }}>{actionError}</p>}
              {actionMessage && <p style={{ margin: 0, fontSize: "12px", color: "var(--success)" }}>{actionMessage}</p>}
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-xs)" }}>
              <label style={{ fontSize: "12px", color: "var(--text-secondary)" }}>Invite additional members (comma or space separated emails)</label>
              <textarea
                rows={2}
                value={inviteEmails}
                onChange={(e) => setInviteEmails(e.target.value)}
                placeholder="user1@example.com, user2@example.com"
                style={{
                  width: "100%",
                  padding: "6px var(--space-md)",
                  borderRadius: "var(--radius-sm)",
                  border: "1px solid var(--border-primary)",
                  fontSize: "13px",
                  backgroundColor: "var(--surface)",
                  color: "var(--text-primary)",
                  resize: "vertical",
                }}
              />
              <div style={{ display: "flex", gap: "var(--space-sm)", alignItems: "center" }}>
                <button
                  type="button"
                  onClick={handleInviteMembers}
                  disabled={inviting}
                  style={{
                    padding: "6px var(--space-md)",
                    backgroundColor: inviting ? "var(--text-disabled)" : "var(--accent-primary)",
                    color: "#fff",
                    border: "none",
                    borderRadius: "var(--radius-sm)",
                    fontSize: "13px",
                    fontWeight: "500",
                    cursor: inviting ? "not-allowed" : "pointer",
                  }}>
                  {inviting ? "Sending…" : "Send Invites"}
                </button>
                {inviteError && <p style={{ margin: 0, fontSize: "12px", color: "var(--danger)" }}>{inviteError}</p>}
                {inviteMessage && <p style={{ margin: 0, fontSize: "12px", color: "var(--success)" }}>{inviteMessage}</p>}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function OpsTenants() {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [reloadToken, setReloadToken] = useState(0);

  const loadTenants = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await fetchOperatorTenants({
        status: statusFilter || undefined,
        search: search || undefined,
      });

      const list = result?.tenants || result || [];
      setTenants(list);
    } catch (err) {
      console.error("[OpsTenants] Failed to load tenants", err);
      setError(err?.message || "Failed to load tenants");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search]);

  useEffect(() => {
    loadTenants();
  }, [loadTenants, reloadToken]);

  const tenantSummary = useMemo(() => {
    if (!Array.isArray(tenants) || tenants.length === 0) {
      return {
        total: 0,
        byStatus: {},
        withClerkOrg: 0,
        withoutClerkOrg: 0,
        totalSeats: 0,
        totalMembers: 0,
      };
    }

    const summary = {
      total: tenants.length,
      byStatus: {},
      withClerkOrg: 0,
      withoutClerkOrg: 0,
      totalSeats: 0,
      totalMembers: 0,
    };

    for (const t of tenants) {
      const status = t.status || "unknown";
      summary.byStatus[status] = (summary.byStatus[status] || 0) + 1;

      const seats = t.seats != null ? Number(t.seats) : null;
      if (Number.isFinite(seats) && seats > 0) {
        summary.totalSeats += seats;
      }

      const memberCount = t.memberCount ?? t.member_count ?? null;
      if (memberCount != null && Number.isFinite(Number(memberCount))) {
        summary.totalMembers += Number(memberCount);
      }

      const hasClerkOrg = Boolean(t.clerkOrgId || t.clerk_org_id);
      if (hasClerkOrg) {
        summary.withClerkOrg += 1;
      } else {
        summary.withoutClerkOrg += 1;
      }
    }

    return summary;
  }, [tenants]);

  const filteredTenants = tenants; // server-side filtering via query params

  return (
    <ErrorBoundary componentName="OpsTenantsPage">
      <div style={{ minHeight: "100vh", background: "var(--bg-secondary)" }}>
        {/* Header */}
        <div style={{ background: "var(--accent-gradient)", borderBottom: "1px solid var(--border-secondary)" }}>
          <div
            style={{
              maxWidth: "var(--container-max-wide)",
              margin: "0 auto",
              padding: "var(--space-2xl) var(--space-xl)",
            }}>
            <h1
              style={{
                fontSize: "32px",
                fontWeight: "400",
                margin: "0 0 var(--space-sm) 0",
                letterSpacing: "-0.5px",
                color: "#fff",
              }}>
              Tenant Operations
            </h1>
            <p style={{ fontSize: "15px", color: "rgba(255,255,255,0.75)", margin: 0 }}>Operator view of all tenants, their seat usage, and Clerk portal health.</p>
          </div>
        </div>

        {/* Main Content */}
        <div
          style={{
            maxWidth: "var(--container-max-wide)",
            margin: "0 auto",
            padding: "var(--space-2xl) var(--space-xl)",
          }}>
          {/* Filters */}
          <div
            style={{
              backgroundColor: "var(--surface)",
              borderRadius: "var(--radius-lg)",
              border: "1px solid var(--border-primary)",
              padding: "var(--space-xl)",
              marginBottom: "var(--space-2xl)",
              boxShadow: "var(--shadow-sm)",
            }}>
            <h2
              style={{
                fontSize: "18px",
                fontWeight: "600",
                color: "var(--text-primary)",
                margin: "0 0 var(--space-lg) 0",
              }}>
              Filter Tenants
            </h2>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "200px 1fr auto",
                gap: "var(--space-md)",
                alignItems: "end",
              }}>
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "12px",
                    color: "var(--text-primary)",
                    marginBottom: "var(--space-xs)",
                  }}>
                  Status
                </label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "var(--space-md)",
                    border: "1px solid var(--border-primary)",
                    borderRadius: "var(--radius-md)",
                    fontSize: "14px",
                    backgroundColor: "var(--surface)",
                    color: "var(--text-primary)",
                  }}>
                  <option value="">All</option>
                  <option value="active">Active</option>
                  <option value="trial">Trial</option>
                  <option value="suspended">Suspended</option>
                  <option value="closed">Closed</option>
                </select>
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "12px",
                    color: "var(--text-primary)",
                    marginBottom: "var(--space-xs)",
                  }}>
                  Search
                </label>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by name or slug"
                  style={{
                    width: "100%",
                    padding: "var(--space-md)",
                    border: "1px solid var(--border-primary)",
                    borderRadius: "var(--radius-md)",
                    fontSize: "14px",
                    backgroundColor: "var(--surface)",
                    color: "var(--text-primary)",
                  }}
                />
              </div>

              <div style={{ display: "flex", gap: "var(--space-sm)" }}>
                <button
                  type="button"
                  onClick={() => setReloadToken((x) => x + 1)}
                  style={{
                    padding: "10px var(--space-lg)",
                    backgroundColor: "var(--surface-elevated)",
                    color: "var(--text-primary)",
                    border: "1px solid var(--border-primary)",
                    borderRadius: "var(--radius-md)",
                    fontSize: "14px",
                    fontWeight: "500",
                    cursor: "pointer",
                  }}>
                  Refresh
                </button>
              </div>
            </div>
          </div>

          {/* Tenant Overview */}
          <div
            style={{
              backgroundColor: "var(--surface)",
              borderRadius: "var(--radius-lg)",
              border: "1px solid var(--border-primary)",
              padding: "var(--space-xl)",
              marginBottom: "var(--space-2xl)",
              boxShadow: "var(--shadow-sm)",
            }}>
            <h2
              style={{
                fontSize: "18px",
                fontWeight: "600",
                color: "var(--text-primary)",
                margin: "0 0 var(--space-sm) 0",
              }}>
              Tenant Overview
            </h2>
            <p
              style={{
                fontSize: "14px",
                color: "var(--text-secondary)",
                marginBottom: "var(--space-lg)",
              }}>
              High-level view of all tenants, their seat usage, and Clerk portal coverage.
            </p>

            {tenantSummary.total === 0 ? (
              <p style={{ fontSize: "14px", color: "var(--text-secondary)", margin: 0 }}>No tenants found yet.</p>
            ) : (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                  gap: "var(--space-lg)",
                }}>
                {/* Totals */}
                <div
                  style={{
                    padding: "var(--space-md)",
                    borderRadius: "var(--radius-md)",
                    backgroundColor: "var(--surface-elevated)",
                    border: "1px solid var(--border-secondary)",
                  }}>
                  <h3
                    style={{
                      fontSize: "14px",
                      fontWeight: "600",
                      margin: "0 0 var(--space-xs) 0",
                      color: "var(--text-secondary)",
                    }}>
                    Totals
                  </h3>
                  <p
                    style={{
                      fontSize: "20px",
                      fontWeight: "600",
                      margin: 0,
                      color: "var(--text-primary)",
                    }}>
                    {tenantSummary.total} tenants
                  </p>
                  <p
                    style={{
                      fontSize: "13px",
                      margin: "var(--space-xs) 0 0 0",
                      color: "var(--text-secondary)",
                    }}>
                    {tenantSummary.totalSeats > 0 ? `${tenantSummary.totalMembers} members across ${tenantSummary.totalSeats} seats` : `${tenantSummary.totalMembers} members`}
                  </p>
                </div>

                {/* Status breakdown */}
                <div
                  style={{
                    padding: "var(--space-md)",
                    borderRadius: "var(--radius-md)",
                    backgroundColor: "var(--surface-elevated)",
                    border: "1px solid var(--border-secondary)",
                  }}>
                  <h3
                    style={{
                      fontSize: "14px",
                      fontWeight: "600",
                      margin: "0 0 var(--space-xs) 0",
                      color: "var(--text-secondary)",
                    }}>
                    By status
                  </h3>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                      gap: "var(--space-xs)",
                      fontSize: "13px",
                    }}>
                    {["active", "trial", "suspended", "closed"].map((statusKey) => {
                      const count = tenantSummary.byStatus[statusKey] || 0;
                      return (
                        <div
                          key={statusKey}
                          style={{
                            opacity: count ? 1 : 0.4,
                            display: "flex",
                            justifyContent: "space-between",
                            gap: "var(--space-xs)",
                          }}>
                          <span>{STATUS_LABELS[statusKey] ?? statusKey}</span>
                          <span>{count}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Clerk coverage */}
                <div
                  style={{
                    padding: "var(--space-md)",
                    borderRadius: "var(--radius-md)",
                    backgroundColor: "var(--surface-elevated)",
                    border: "1px solid var(--border-secondary)",
                  }}>
                  <h3
                    style={{
                      fontSize: "14px",
                      fontWeight: "600",
                      margin: "0 0 var(--space-xs) 0",
                      color: "var(--text-secondary)",
                    }}>
                    Clerk coverage
                  </h3>
                  <p
                    style={{
                      fontSize: "13px",
                      margin: "var(--space-xs) 0",
                      color: "var(--text-secondary)",
                    }}>
                    {tenantSummary.withClerkOrg} tenants linked to a Clerk organization
                  </p>
                  <p
                    style={{
                      fontSize: "13px",
                      margin: 0,
                      color: "var(--text-secondary)",
                    }}>
                    {tenantSummary.withoutClerkOrg} tenants without a linked Clerk organization
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Tenants List */}
          <div
            style={{
              backgroundColor: "var(--surface)",
              borderRadius: "var(--radius-lg)",
              border: "1px solid var(--border-primary)",
              padding: "var(--space-xl)",
              boxShadow: "var(--shadow-sm)",
            }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "var(--space-lg)",
                gap: "var(--space-md)",
                flexWrap: "wrap",
              }}>
              <h2
                style={{
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "var(--text-primary)",
                  margin: 0,
                }}>
                Tenants
              </h2>
              {loading && (
                <span
                  style={{
                    fontSize: "13px",
                    color: "var(--text-secondary)",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                  }}>
                  <span
                    style={{
                      width: "14px",
                      height: "14px",
                      borderRadius: "999px",
                      border: "2px solid var(--border-primary)",
                      borderTopColor: "var(--accent-primary)",
                      animation: "spin 0.8s linear infinite",
                    }}
                  />
                  Loading…
                </span>
              )}
            </div>

            {error && (
              <div
                style={{
                  marginBottom: "var(--space-lg)",
                  padding: "var(--space-md)",
                  borderRadius: "var(--radius-md)",
                  border: "1px solid var(--danger)",
                  backgroundColor: "var(--danger-bg)",
                  color: "var(--danger)",
                  fontSize: "13px",
                }}>
                {error}
              </div>
            )}

            {!loading && !error && !filteredTenants.length && <p style={{ fontSize: "14px", color: "var(--text-secondary)", margin: 0 }}>No tenants found.</p>}

            {filteredTenants.length > 0 && (
              <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-lg)" }}>
                {filteredTenants.map((tenant) => (
                  <TenantCard key={tenant.id} tenant={tenant} onRefresh={loadTenants} />
                ))}
              </div>
            )}
          </div>
        </div>

        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    </ErrorBoundary>
  );
}
